/**
 * 錯誤處理工具函數
 * 統一處理錯誤消息提取和格式化
 */

/**
 * 從錯誤對象中提取錯誤消息
 * 支持多種錯誤格式：Axios 錯誤、普通 Error、字符串等
 *
 * @param error - 錯誤對象
 * @param defaultMessage - 默認錯誤消息
 * @returns 錯誤消息字符串
 */
export const extractErrorMessage = (
  error: unknown,
  defaultMessage: string = '發生錯誤'
): string => {
  // 如果是字符串，直接返回
  if (typeof error === 'string') {
    return error;
  }

  // 如果是 Error 對象
  if (error instanceof Error) {
    return error.message || defaultMessage;
  }

  // 如果是 Axios 錯誤或其他對象
  if (typeof error === 'object' && error !== null) {
    const axiosError = error as any;

    // 優先使用標準 ApiResponse 格式的錯誤消息
    if (axiosError.response?.data) {
      const data = axiosError.response.data;

      // 標準 ApiResponse 格式
      if (data.message) {
        return data.message;
      }

      // 其他格式
      if (data.error) {
        return data.error;
      }
    }

    // 使用 Axios 錯誤消息
    if (axiosError.message) {
      return axiosError.message;
    }
  }

  return defaultMessage;
};

/**
 * 格式化錯誤消息用於顯示
 * 在操作名稱前加上錯誤消息
 *
 * @param error - 錯誤對象
 * @param operation - 操作名稱（如 '創建用戶'、'更新數據'）
 * @returns 格式化後的錯誤消息
 */
export const formatErrorForDisplay = (
  error: unknown,
  operation: string
): string => {
  const errorMessage = extractErrorMessage(error);

  // 如果錯誤消息已經包含操作名稱，直接返回
  if (errorMessage.includes(operation)) {
    return errorMessage;
  }

  return `${operation}失敗: ${errorMessage}`;
};

/**
 * 判斷是否為網絡錯誤
 */
export const isNetworkError = (error: unknown): boolean => {
  if (typeof error === 'object' && error !== null) {
    const axiosError = error as any;
    return !axiosError.response && axiosError.request;
  }
  return false;
};

/**
 * 判斷是否為認證錯誤（401）
 */
export const isAuthError = (error: unknown): boolean => {
  if (typeof error === 'object' && error !== null) {
    const axiosError = error as any;
    return axiosError.response?.status === 401;
  }
  return false;
};

/**
 * 判斷是否為權限錯誤（403）
 */
export const isPermissionError = (error: unknown): boolean => {
  if (typeof error === 'object' && error !== null) {
    const axiosError = error as any;
    return axiosError.response?.status === 403;
  }
  return false;
};

